import {
  AmountCell,
  Badge,
  DateCell,
  NameCell,
  NotScheduledCell,
  SubduedCell,
} from "@";
import type { ColumnDef } from "@tanstack/react-table";
import type { UnassignedVerificationStage } from "../types.ts";
import { VerificationStageType } from "@/features/operations/verifications/types.ts";
import { Link } from "react-router-dom";

// Order ID — brand-purple chip linking to the donation, matches the verify
// page's OrderIdLink so the two screens read alike.
const OrderIdLink = ({ orderId }: { orderId: number }) => (
  <Link
    to={`/donations?toDate=&fromDate=&id=${orderId}`}
    className="inline-flex items-center rounded-md bg-purple-50 px-2 py-0.5 font-mono text-[12px] font-semibold tabular-nums text-purple-900 ring-1 ring-purple-100 transition-colors hover:bg-purple-100 dark:bg-purple-950/40 dark:text-purple-300 dark:ring-purple-800/40 dark:hover:bg-purple-900/40"
  >
    {orderId}
  </Link>
);

// Stage column — number-prefixed name + first-stage and manual flags below.
const StageCell = ({
  stageNumber,
  stageName,
  isFirstStageVerified,
  type,
}: {
  stageNumber?: number;
  stageName?: string;
  isFirstStageVerified?: boolean;
  type?: number;
}) => (
  <div className="flex flex-col gap-1.5">
    <span className="inline-flex items-baseline gap-1 font-medium tracking-tight text-gray-900 dark:text-gray-100">
      <span className="tabular-nums text-gray-500 dark:text-gray-500">
        {stageNumber}
      </span>
      <span className="text-gray-400 dark:text-gray-600">·</span>
      <span>{stageName}</span>
    </span>
    {isFirstStageVerified && (
      <span>
        <Badge title="تم توثيق المرحلة الأولى" color="green" />
      </span>
    )}
    {type === VerificationStageType.Manual && (
      <span>
        <Badge title="توثيق يدوي" color="dark" />
      </span>
    )}
  </div>
);

// TanStack table columns format
export const tableColumns: ColumnDef<UnassignedVerificationStage>[] = [
  {
    id: "orderId",
    header: "رقم الطلب",
    size: 90,
    cell: ({ row }) => <OrderIdLink orderId={row.original.orderId} />,
  },
  {
    size: 110,
    id: "userName",
    header: "العميل",
    cell: ({ row }) => <NameCell name={row.original.userName} />,
  },
  {
    size: 100,
    id: "stageName",
    header: "المرحلة",
    cell: ({ row }) => (
      <StageCell
        stageNumber={row.original.stageNumber}
        stageName={row.original.stageName}
        isFirstStageVerified={row.original.isFirstStageVerified}
        type={row.original.type}
      />
    ),
  },
  {
    size: 160,
    id: "attributeName",
    header: "فئة الطلب",
    cell: ({ row }) => <SubduedCell value={row.original.attributeName} />,
  },
  {
    id: "projectName",
    header: "المشروع",
    size: 150,
    cell: ({ row }) => <NameCell name={row.original.projectName} />,
  },
  {
    size: 110,
    id: "smsSendingDate",
    header: "تاريخ التنفيذ",
    cell: ({ row }) => {
      const date = row.original.smsSendingDate;
      if (!date) {
        return <NotScheduledCell id={`donations-${row.original.orderId}`} />;
      }
      return <DateCell value={date} />;
    },
  },
  {
    id: "createdOn",
    size: 110,
    header: "تاريخ الإنشاء",
    cell: ({ row }) => <DateCell value={row.original.createdOn} />,
  },
  {
    size: 110,
    id: "orderItemAmount",
    header: "المبلغ",
    cell: ({ row }) => (
      <AmountCell value={row.original.orderItemAmount} tone="emerald" />
    ),
  },
];

export default tableColumns;
